import { setDiagnosticsEffect } from '@codemirror/lint';
import type { Diagnostic } from '@codemirror/lint';
import { RangeSet, StateField } from '@codemirror/state';
import type { Extension, Text, Transaction } from '@codemirror/state';
import { GutterMarker, gutterLineClass } from '@codemirror/view';

/**
 * Class applied to every gutter cell on a line with an `error` diagnostic.
 *
 * @internal
 */
export const criticalGutterLineClass = 'cm-gutterElement-critical';

class CriticalGutterLineMarker extends GutterMarker {
  override elementClass = criticalGutterLineClass;
}

const criticalGutterLineMarker = new CriticalGutterLineMarker();

/**
 * Builds gutter markers for the lines where `error` diagnostics start.
 *
 * @param doc - The document associated with the diagnostics.
 * @param diagnostics - The diagnostics to inspect.
 * @returns The gutter markers for lines containing errors.
 * @internal
 */
export function _buildCriticalGutterLineMarkers(
  doc: Text,
  diagnostics: readonly Diagnostic[],
): RangeSet<GutterMarker> {
  const lineStarts = new Set<number>();

  for (const diagnostic of diagnostics) {
    if (diagnostic.severity !== 'error') {
      continue;
    }
    // Consumer-provided diagnostics may point outside the document, which would
    // make `lineAt` throw.
    if (diagnostic.from < 0 || diagnostic.from > doc.length) {
      continue;
    }
    lineStarts.add(doc.lineAt(diagnostic.from).from);
  }

  return RangeSet.of(
    [...lineStarts].map((from) => criticalGutterLineMarker.range(from)),
    true,
  );
}

/**
 * Marks the gutter cells of the lines carrying an `error` diagnostic.
 *
 * @internal
 */
export const criticalGutterLines: Extension = StateField.define<
  RangeSet<GutterMarker>
>({
  create: (): RangeSet<GutterMarker> => RangeSet.empty,
  update(
    markers: RangeSet<GutterMarker>,
    transaction: Transaction,
  ): RangeSet<GutterMarker> {
    // Keeps the markers on their lines while the linter has not re-run yet.
    let updated = transaction.docChanged
      ? markers.map(transaction.changes)
      : markers;

    // A transaction can carry more than one of these; the last one wins, which
    // is how CodeMirror's own lint state resolves them too.
    for (const effect of transaction.effects) {
      if (effect.is(setDiagnosticsEffect)) {
        updated = _buildCriticalGutterLineMarkers(
          transaction.newDoc,
          effect.value,
        );
      }
    }

    return updated;
  },
  provide: (field) => gutterLineClass.from(field),
});
